import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useWatch } from 'react-hook-form';
import type { FormHtmlTemplateNode } from '../types/layout';
import { useFormEngineForm } from '../context/FormEngineContext';

/**
 * Renders interpolated HTML via dangerouslySetInnerHTML.
 * WARNING: Only use with trusted template sources. Sanitize server HTML before
 * storing in schema/meta — this renderer does not sanitize markup.
 */
const HtmlContainer = styled.div`
  width: 100%;
`;

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

/**
 * Interpolate `{token}` placeholders in an HTML template with form values.
 *
 * Signature preview: when a bound field value is a base64 data URL (`data:image/...`),
 * it is rendered as an `<img>` tag so html-template nodes can display signature pads.
 * Use `tokens` to map template placeholders to signature field paths.
 */
export function interpolateHtmlTemplate(
  template: string,
  values: Record<string, unknown>,
  tokens?: Record<string, string>,
): string {
  return template.replace(/\{([^}]+)\}/g, (_, tokenKey: string) => {
    const fieldPath = tokens?.[tokenKey] ?? tokenKey;
    const val = getNestedValue(values, fieldPath);
    if (val == null || val === '') return '';
    if (typeof val === 'string' && val.startsWith('data:image/')) {
      return `<img src="${val}" alt="${tokenKey}" style="max-width:100%;height:auto;" />`;
    }
    return String(val);
  });
}

interface FormHtmlTemplateRendererProps {
  node: FormHtmlTemplateNode;
}

export const FormHtmlTemplateRenderer = React.forwardRef<HTMLDivElement, FormHtmlTemplateRendererProps>(
  function FormHtmlTemplateRenderer({ node }, ref): React.ReactElement {
    const { form } = useFormEngineForm();

    const watchPaths = useMemo(() => {
      const paths = new Set<string>();
      if (node.tokens) {
        for (const fieldPath of Object.values(node.tokens)) {
          paths.add(fieldPath);
        }
      } else {
        const matches = node.template.matchAll(/\{([^}]+)\}/g);
        for (const match of matches) {
          paths.add(match[1]);
        }
      }
      return [...paths];
    }, [node.template, node.tokens]);

    const watchedValues = useWatch({
      control: form.control,
      name: watchPaths as never[],
      disabled: watchPaths.length === 0,
    });

    const html = useMemo(() => {
      const values = form.getValues() as Record<string, unknown>;
      return interpolateHtmlTemplate(node.template, values, node.tokens);
    }, [form, node.template, node.tokens, watchedValues]);

    return (
      <HtmlContainer
        ref={ref}
        data-form-html-template=''
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  },
);

FormHtmlTemplateRenderer.displayName = 'FormHtmlTemplateRenderer';
